import { createSlice } from '@reduxjs/toolkit';

// Initial state
const initialState = {
  theme: 'light', // 'light' | 'dark'
  sidebarOpen: true,
  notifications: [],
  loading: false,
  modals: {
    isOpen: false,
    type: null,
    data: null,
  },
};

// UI slice
const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    setTheme: (state, action) => {
      state.theme = action.payload;
      // Update localStorage
      if (typeof window !== 'undefined') {
        localStorage.setItem('theme', action.payload);
        document.documentElement.classList.toggle('dark', action.payload === 'dark');
      }
    },
    toggleTheme: (state) => {
      state.theme = state.theme === 'light' ? 'dark' : 'light';
      // Update localStorage
      if (typeof window !== 'undefined') {
        localStorage.setItem('theme', state.theme);
        document.documentElement.classList.toggle('dark', state.theme === 'dark');
      }
    },
    toggleSidebar: (state) => {
      state.sidebarOpen = !state.sidebarOpen;
    },
    setSidebarOpen: (state, action) => {
      state.sidebarOpen = action.payload;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    addNotification: (state, action) => {
      const notification = {
        id: Date.now() + Math.random(),
        timestamp: new Date().toISOString(),
        ...action.payload,
      };
      state.notifications.unshift(notification);
    },
    addToast: (state, action) => {
      const toast = {
        id: Date.now() + Math.random(),
        type: action.payload.type || 'info', // success, error, warning, info
        message: action.payload.message,
        timestamp: new Date().toISOString(),
        autoClose: action.payload.autoClose !== false,
        duration: action.payload.duration || 5000,
      };
      state.notifications.unshift(toast);
    },
    showToast: (state, action) => {
      const { type, message, duration = 5000 } = action.payload;
      const toast = {
        id: Date.now() + Math.random(),
        type,
        message,
        timestamp: new Date().toISOString(),
        autoClose: true,
        duration,
      };
      state.notifications.unshift(toast);
    },
    showSuccess: (state, action) => {
      const toast = {
        id: Date.now() + Math.random(),
        type: 'success',
        message: action.payload,
        timestamp: new Date().toISOString(),
        autoClose: true,
        duration: 5000,
      };
      state.notifications.unshift(toast);
    },
    showError: (state, action) => {
      const toast = {
        id: Date.now() + Math.random(),
        type: 'error',
        message: action.payload,
        timestamp: new Date().toISOString(),
        autoClose: true,
        duration: 5000,
      };
      state.notifications.unshift(toast);
    },
    showWarning: (state, action) => {
      const toast = {
        id: Date.now() + Math.random(),
        type: 'warning',
        message: action.payload,
        timestamp: new Date().toISOString(),
        autoClose: true,
        duration: 5000,
      };
      state.notifications.unshift(toast);
    },
    showInfo: (state, action) => {
      const toast = {
        id: Date.now() + Math.random(),
        type: 'info',
        message: action.payload,
        timestamp: new Date().toISOString(),
        autoClose: true,
        duration: 5000,
      };
      state.notifications.unshift(toast);
    },
    removeNotification: (state, action) => {
      state.notifications = state.notifications.filter(
        notification => notification.id !== action.payload
      );
    },
    clearNotifications: (state) => {
      state.notifications = [];
    },
    openModal: (state, action) => {
      state.modals = {
        isOpen: true,
        type: action.payload.type,
        data: action.payload.data || null,
      };
    },
    closeModal: (state) => {
      state.modals = {
        isOpen: false,
        type: null,
        data: null,
      };
    },
    initializeTheme: (state) => {
      if (typeof window !== 'undefined') {
        const savedTheme = localStorage.getItem('theme');
        // Always default to light mode if no saved preference exists
        const theme = savedTheme || 'light';
        
        state.theme = theme;
        document.documentElement.classList.toggle('dark', theme === 'dark');
      }
    },
  },
});

export const {
  setTheme,
  toggleTheme,
  toggleSidebar,
  setSidebarOpen,
  setLoading,
  addNotification,
  addToast,
  showToast,
  showSuccess,
  showError,
  showWarning,
  showInfo,
  removeNotification,
  clearNotifications,
  openModal,
  closeModal,
  initializeTheme,
} = uiSlice.actions;

export default uiSlice.reducer;
